import { FC, useMemo } from "react"
import {
  Container,
  Typography,
  AnimatedLoadingIcon
} from "@eventbrite/marmalade"
import classNames from "classnames"

// components
import { MyTickets } from "../PersonalizationComponents/Attendee/MyTickets/MyTickets"
import { MyEvents } from "../PersonalizationComponents/Organizers/MyEvents"
import { LogIn } from "../LogIn/LogIn"
import * as MyTicketsStyles from "../PersonalizationComponents/Attendee/MyTickets/MyTickets.module.scss"

// hooks
import {
  usePersonalizationComponent,
  PersonalizationComponentType
} from "../../hooks/usePersonalizationComponent"

type PersonalizationProps = {
  noTicketsFoundActionComplex?: any
  ticketsFoundActionComplex?: any
  messageNoUpcomingOrders?: any
  searchingForTickets?: any
  noEventsFoundActionComplex?: any
  eventsFoundActionComplex?: any
  searchingForEvents?: any
  messageNoUpcomingEvents?: any
  loginButtonMicrocopy?: any
  loginContentMicrocopy?: any
  loginHeaderMicrocopy?: any

  // Configuration
  audienceOverride?: string
  columnLayout?: boolean
  mobileSizeOnly?: boolean
  containerClassName?: string
  mode?: "homepage" | "article" | "contact-us" // Add mode support
}

export const PersonalizationSection: FC<PersonalizationProps> = (props) => {
  const { activePersonalizationComponent, userProfile } =
    usePersonalizationComponent(
      props.audienceOverride,
      props.mode || "homepage"
    )

  // Type-safe data normalization - convert any input to the expected component format
  const normalizedProps = useMemo(() => {
    const normalize = (data: any) => {
      // Safely extract the data regardless of the original structure
      if (!data) return data

      // Handle different GraphQL query structures
      if (typeof data === "object") {
        return data // Pass through - components will handle the structure
      }

      return data
    }

    return {
      // Ticket props
      noTicketsFoundActionComplex: normalize(props.noTicketsFoundActionComplex),
      ticketsFoundActionComplex: normalize(props.ticketsFoundActionComplex),
      messageNoUpcomingOrders: normalize(props.messageNoUpcomingOrders),
      searchingForTickets: normalize(props.searchingForTickets),

      // Event props
      noEventsFoundActionComplex: normalize(props.noEventsFoundActionComplex),
      eventsFoundActionComplex: normalize(props.eventsFoundActionComplex),
      searchingForEvents: normalize(props.searchingForEvents),
      messageNoUpcomingEvents: normalize(props.messageNoUpcomingEvents),

      // Login props
      loginButtonMicrocopy: normalize(props.loginButtonMicrocopy),
      loginContentMicrocopy: normalize(props.loginContentMicrocopy),
      loginHeaderMicrocopy: normalize(props.loginHeaderMicrocopy)
    }
  }, [props])

  const personalizationComponents = useMemo(() => {
    const components: Record<
      Exclude<PersonalizationComponentType, null>,
      JSX.Element
    > = {
      login: (
        <LogIn
          loginButtonMicrocopy={normalizedProps.loginButtonMicrocopy}
          loginContentMicrocopy={normalizedProps.loginContentMicrocopy}
          loginHeaderMicrocopy={normalizedProps.loginHeaderMicrocopy}
          mobileSizeOnly={props.mobileSizeOnly}
        />
      ),
      loading: (
        <Container
          className={classNames(MyTicketsStyles.myticketsOuterContainer, {
            [`${MyTicketsStyles.myticketsColumnContainer}`]: props.columnLayout
              ? true
              : false
          })}
          style={{
            minHeight: props.columnLayout ? "280px" : "240px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          <AnimatedLoadingIcon
            color="#3659E3"
            className={MyTicketsStyles.loaderSvg}
          />
          <Typography
            variant="body-sm"
            style={{ marginTop: "1rem", color: "#C1BDC7" }}
          >
            Loading your content...
          </Typography>
        </Container>
      ),
      attendee: (
        <MyTickets
          orders={userProfile?.orders}
          columnLayout={props.columnLayout}
          noTicketsFoundActionComplex={
            normalizedProps.noTicketsFoundActionComplex
          }
          ticketsFoundActionComplex={normalizedProps.ticketsFoundActionComplex}
          messageNoUpcomingOrders={normalizedProps.messageNoUpcomingOrders}
          searchingForTickets={normalizedProps.searchingForTickets}
        />
      ),
      organizer: (
        <MyEvents
          events={userProfile?.allData?.userEventsData?.events}
          columnLayout={props.columnLayout}
          noEventsFoundActionComplex={
            normalizedProps.noEventsFoundActionComplex
          }
          eventsFoundActionComplex={normalizedProps.eventsFoundActionComplex}
          searchingForEvents={normalizedProps.searchingForEvents}
          messageNoUpcomingEvents={normalizedProps.messageNoUpcomingEvents}
        />
      )
    }
    return components
  }, [normalizedProps, userProfile, props.columnLayout, props.mobileSizeOnly])

  if (!activePersonalizationComponent) {
    return null
  }

  const ComponentToRender =
    personalizationComponents[
      activePersonalizationComponent as Exclude<
        PersonalizationComponentType,
        null
      >
    ]

  return (
    <div className={props.containerClassName}>
      {ComponentToRender}
    </div>
  )
}
